import pkceService from './pkceService';
import debugPKCE from '../utils/debugPKCE';
import { getEffectiveTenantOrigin } from '../config';
import { postOidcTokenRequest } from '../utils/oidcTokenExchange';

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  id_token?: string;
  refresh_token?: string;
}

interface OidcConfig {
  clientId: string;
  tenantId: string;
  authority: string;
  redirectUri: string;
  requirePkce: boolean;
  // Add discovered endpoints
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  userinfoEndpoint?: string;
  endSessionEndpoint?: string;
  jwksUri?: string;
  issuer?: string;
  discoveryUrl?: string;
}

class AuthCallbackService {
  /**
   * Handle OAuth callback and exchange authorization code for tokens
   */
  async handleCallback(config: OidcConfig): Promise<TokenResponse | null> {
    try {
      if (process.env.NODE_ENV !== 'production') {
        debugPKCE.validateCallbackURL();
        debugPKCE.logPKCEState();
      }
      
      // Get the authorization code from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      // Handle OAuth errors
      if (error) {
        console.error('OAuth error:', error, errorDescription);
        throw new Error(`OAuth error: ${error} - ${errorDescription}`);
      }

      if (!code) {
        console.error('No authorization code received');
        return null;
      }

      // Exchange code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code, config);
      
      // Clear PKCE parameters after successful exchange
      if (config.requirePkce) {
        pkceService.clearPKCEParameters();
      }

      return tokenResponse;
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      // Clear PKCE parameters on error as well
      if (config.requirePkce) {
        pkceService.clearPKCEParameters();
      }
      throw error;
    }
  }

  /**
   * Exchange authorization code for access tokens
   */
  private async exchangeCodeForTokens(code: string, config: OidcConfig): Promise<TokenResponse> {
    // Use discovered token endpoint or fallback to Microsoft format
    const tokenEndpoint = config.tokenEndpoint || 
      `${config.authority}/oauth2/v2.0/token`;
    
    // Construct the full redirect URI (same as we do during authorization)
    const redirectUri = `${getEffectiveTenantOrigin()}${config.redirectUri}`;

    const params = new URLSearchParams({
      client_id: config.clientId,
      code: code,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code'
    });

    if (config.requirePkce) {
      const codeVerifier = pkceService.getCodeVerifier();
      if (!codeVerifier) {
        throw new Error('Code verifier not found. PKCE flow may have been interrupted.');
      }
      params.append('code_verifier', codeVerifier);
    }

    const response = await postOidcTokenRequest(tokenEndpoint, params);

    if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      error_description?: string;
    };
      console.error('Token exchange failed:', response.status, errorData);
      throw new Error(`Token exchange failed: ${response.status} - ${errorData.error_description || 'Unknown error'}`);
    }

    const tokenData = await response.json() as TokenResponse;

    return tokenData;
  }

  /**
   * Check if current URL contains OAuth callback parameters
   */
  isCallbackUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  }

  /**
   * Clean up callback parameters from URL
   */
  cleanupCallbackUrl(): void {
    if (this.isCallbackUrl()) {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      
      // Update URL without reloading the page
      window.history.replaceState({}, document.title, url.toString());
    }
  }
}

// Export a singleton instance
export const authCallbackService = new AuthCallbackService();
export default authCallbackService; 